import { useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  Option,
  Stack,
  useTheme,
  Grid,
  CircularProgress,
} from '@mui/joy';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList } from 'recharts';
import useUser from './auth/useUser';
import { Candidates } from './JobCandidates';

const GET_CANDIDATES_STATS = gql`
  query GetCandidatesStatsForJob($jobId: String!, $startDate: Float, $endDate: Float) {
    getCandidatesStatsForJob(jobId: $jobId, startDate: $startDate, endDate: $endDate) {
      total
      withQuestionnairePassed
      filteredOut
      finished
    }
  }
`;

const Dashboard = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  console.log({ selectedJob });

  // get from url params
  const jobIdFromUrl = new URLSearchParams(window.location.search).get('jobId');

  // Get current date for default range
  const currentDate = new Date();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  const currentYear = String(currentDate.getFullYear());

  const [dateRange, setDateRange] = useState({
    fromMonth: currentMonth,
    fromYear: currentYear,
    toMonth: currentMonth,
    toYear: currentYear
  });
  // const [sortBy, setSortBy] = useState('By score (ASC)');

  const { user } = useUser();
  const jobs = useMemo(() => user?.client?.jobs || [], [user]);
  const client = useMemo(() => user?.client || null, [user]);
  const theme = useTheme();

  // Enhanced job statistics
  const jobStats = useMemo(() => {
    const totalJobs = jobs.length;
    const publishedJobs = jobs.filter(job => job.published).length;
    const { total: totalApplications = 0, finished: totalCandidates = 0 } = client?.candidatesStats || {};

    return {
      totalJobs,
      publishedJobs,
      totalApplications,
      totalCandidates,
      draftJobs: totalJobs - publishedJobs
    };
  }, [jobs, client]);

  // Calculate available date range and months from selected job's applications
  const availableDateInfo = useMemo(() => {
    if (!selectedJob?.candidatesStats?.firstApplicationDate || !selectedJob?.candidatesStats?.lastApplicationDate) {
      // Default range if no job selected or no applications
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      return {
        availableYears: [currentYear],
        availableMonths: [currentMonth],
        hasData: false
      };
    }
    // epoch
    const { firstApplicationDate, lastApplicationDate } = selectedJob.candidatesStats;
    const start = new Date(parseInt(firstApplicationDate));
    const end = new Date(parseInt(lastApplicationDate));

    const years = [];
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
      years.push(y);
    }

    const monthsInData = [];
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);

    while (current <= last) {
      monthsInData.push({
        month: current.getMonth() + 1,
        year: current.getFullYear()
      });
      current.setMonth(current.getMonth() + 1);
    }

    // For month filtering based on selected year
    const getAvailableMonthsForYear = (year) => {
      const targetYear = parseInt(year);
      return monthsInData
        .filter(d => d.year === targetYear)
        .map(d => d.month);
    };

    return {
      availableYears: years.length > 0 ? years : [new Date().getFullYear()],
      availableMonths: monthsInData,
      getAvailableMonthsForYear,
      hasData: true
    };
  }, [selectedJob]);

  // Get available months for the selected years
  const availableFromMonths = useMemo(() => {
    if (!availableDateInfo.hasData) return Array.from({ length: 12 }, (_, i) => i + 1);
    return availableDateInfo.getAvailableMonthsForYear(dateRange.fromYear);
  }, [availableDateInfo, dateRange.fromYear]);

  const availableToMonths = useMemo(() => {
    if (!availableDateInfo.hasData) return Array.from({ length: 12 }, (_, i) => i + 1);
    return availableDateInfo.getAvailableMonthsForYear(dateRange.toYear);
  }, [availableDateInfo, dateRange.toYear]);

  // Update date range when job selection changes
  useEffect(() => {
    if (availableDateInfo.hasData && selectedJob?.candidatesStats) {
      const { firstApplicationDate, lastApplicationDate } = selectedJob.candidatesStats;
      const minDate = new Date(parseInt(firstApplicationDate));
      const maxDate = new Date(parseInt(lastApplicationDate));

      setDateRange({
        fromMonth: String(minDate.getMonth() + 1).padStart(2, '0'),
        fromYear: String(minDate.getFullYear()),
        toMonth: String(maxDate.getMonth() + 1).padStart(2, '0'),
        toYear: String(maxDate.getFullYear())
      });
    }
  }, [availableDateInfo, selectedJob]);

  const startDate = useMemo(() => {
    return new Date(parseInt(dateRange.fromYear), parseInt(dateRange.fromMonth) - 1, 1).getTime();
  }, [dateRange.fromYear, dateRange.fromMonth]);

  const endDate = useMemo(() => {
    return new Date(parseInt(dateRange.toYear), parseInt(dateRange.toMonth), 0, 23, 59, 59, 999).getTime();
  }, [dateRange.toYear, dateRange.toMonth]);

  const { data: statsData, loading: statsLoading } = useQuery(GET_CANDIDATES_STATS, {
    variables: {
      jobId: selectedJob?._id,
      startDate,
      endDate
    },
    skip: !selectedJob?._id
  });

  const currentJobCandidatesStats = useMemo(() => statsData?.getCandidatesStatsForJob || {}, [statsData]);

  // Mock data for statistics - replace with real data from your API
  const statisticsData = useMemo(() => {
    const { total, withQuestionnairePassed, filteredOut, finished } = currentJobCandidatesStats || {};

    if (total === 0) {
      return [
        { name: 'Created profile', value: 0, percentage: 0, label: '0 (0%)' },
        { name: 'Answered screening questions', value: 0, filtered: 0, percentage: 0, label: '0 (0%)', filteredLabel: '' },
        { name: 'Recorded video interview', value: 0, percentage: 0, label: '0 (0%)' }
      ];
    }

    const profilePercentage = Math.round((total / total) * 100);
    const questionnairePercentage = Math.round((withQuestionnairePassed / total) * 100);
    const interviewPercentage = Math.round((finished / total) * 100);
    const filteredPercentage = Math.round((filteredOut / total) * 100);

    return [
      {
        name: 'Created profile',
        value: total,
        percentage: profilePercentage,
        label: `${total} (${profilePercentage}%)`
      },
      {
        name: 'Answered screening questions',
        value: withQuestionnairePassed,
        filtered: filteredOut,
        percentage: questionnairePercentage,
        label: `${withQuestionnairePassed} (${questionnairePercentage}%)`,
        filteredLabel: filteredOut > 0 ? `${filteredOut} (${filteredPercentage}%)` : ''
      },
      {
        name: 'Recorded video interview',
        value: finished,
        percentage: interviewPercentage,
        label: `${finished} (${interviewPercentage}%)`
      }
    ];
  }, [currentJobCandidatesStats]);

  console.log("Statistics Data", statisticsData);

  useEffect(() => {
    if (selectedJob)
      return;
    if (jobs.length > 0) {
      if (jobIdFromUrl) {
        const job = jobs.find(job => job._id === jobIdFromUrl);
        if (job) {
          setSelectedJob(job);
        }
      } else {
        setSelectedJob(jobs[0]);
      }
    }
  }, [jobs, selectedJob, jobIdFromUrl]);

  useEffect(() => {
    window.gtag('event', 'dashboard_viewed', {});
  }, []);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography level="h1" mb={3}>
        Dashboard
      </Typography>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="primary" sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography level="h2" color="primary">
                {jobStats.totalJobs}
              </Typography>
              <Typography level="body-sm">
                Total Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="success" sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography level="h2" color="success">
                {jobStats.publishedJobs}
              </Typography>
              <Typography level="body-sm">
                Published
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="warning" sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography level="h2" color="warning">
                {jobStats.totalApplications}
              </Typography>
              <Typography level="body-sm">
                Total Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="neutral" sx={{ textAlign: 'center' }}>
            <CardContent>
              <Typography level="h2">
                {jobStats.totalCandidates}
              </Typography>
              <Typography level="body-sm">
                Total Candidates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="title-md" mb={3}>
            Filter Options
          </Typography>
          <Stack direction="column" spacing={1} alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Box sx={{ minWidth: 200 }}>
              <Typography level="body-sm" mb={1.5} color="neutral">
                Position
              </Typography>
              <Select
                value={selectedJob}
                onChange={(e, value) => setSelectedJob(value)}
                sx={{ width: '100%' }}
                variant="outlined"
                size="sm"
                placeholder="Select position"
              >
                {jobs.map(job => <Option key={job._id} value={job}>{job.title}</Option>)}
              </Select>
            </Box>

            <Box sx={{ minWidth: { xs: '100%', sm: 320 } }}>
              <Typography level="body-sm" mb={1.5} color="neutral">
                Date Range
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Select
                  value={dateRange.fromMonth}
                  onChange={(e, value) => handleDateChange('fromMonth', value)}
                  sx={{ minWidth: { xs: 60, sm: 70 } }}
                  variant="outlined"
                  size="sm"
                  placeholder="MM"
                >
                  {availableFromMonths.map((month) => (
                    <Option key={month} value={String(month).padStart(2, '0')}>
                      {String(month).padStart(2, '0')}
                    </Option>
                  ))}
                </Select>

                <Select
                  value={dateRange.fromYear}
                  onChange={(e, value) => handleDateChange('fromYear', value)}
                  sx={{ minWidth: { xs: 75, sm: 85 } }}
                  variant="outlined"
                  size="sm"
                  placeholder="YYYY"
                >
                  {availableDateInfo.availableYears.map(year => (
                    <Option key={year} value={String(year)}>{year}</Option>
                  ))}
                </Select>

                <Typography level="body-sm" color="neutral" sx={{ px: 1, minWidth: 'fit-content' }}>
                  to
                </Typography>

                <Select
                  value={dateRange.toMonth}
                  onChange={(e, value) => handleDateChange('toMonth', value)}
                  sx={{ minWidth: { xs: 60, sm: 70 } }}
                  variant="outlined"
                  size="sm"
                  placeholder="MM"
                >
                  {availableToMonths.map((month) => (
                    <Option key={month} value={String(month).padStart(2, '0')}>
                      {String(month).padStart(2, '0')}
                    </Option>
                  ))}
                </Select>

                <Select
                  value={dateRange.toYear}
                  onChange={(e, value) => handleDateChange('toYear', value)}
                  sx={{ minWidth: { xs: 75, sm: 85 } }}
                  variant="outlined"
                  size="sm"
                  placeholder="YYYY"
                >
                  {availableDateInfo.availableYears.map(year => (
                    <Option key={year} value={String(year)}>{year}</Option>
                  ))}
                </Select>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Statistics Section */}
      <Typography level="h2" mb={2}>
        Statistics
      </Typography>

      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ width: '100%', height: 400, position: 'relative' }}>
            {statsLoading ? (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1
              }}>
                <CircularProgress />
              </Box>
            ) : null}
            <ResponsiveContainer>
              <BarChart data={statisticsData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} fill={theme.palette.primary[400]}>
                  <LabelList
                    dataKey="label"
                    position="center"
                    fill="#ffffff"
                    fontSize={14}
                    fontWeight="bold"
                  />
                </Bar>
                {/* Filtered out section */}
                <Bar dataKey="filtered" stackId="a" fill={theme.palette.danger[400]} radius={[4, 4, 0, 0]}>
                  <LabelList
                    dataKey="filteredLabel"
                    position="center"
                    fill="#ffffff"
                    fontSize={14}
                    fontWeight="bold"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Legend for filtered out */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'danger.400' }} />
                <Typography level="body-sm">Filtered out</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Candidates Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={2}>
        <Typography level="h2">
          Candidates
        </Typography>
        {/* <Box sx={{ minWidth: 180 }}>
          <Typography level="body-sm" mb={1.5} color="neutral">
            Sort by
          </Typography>
          <Select
            value={sortBy}
            onChange={(e, value) => setSortBy(value)}
            sx={{ width: '100%' }}
            variant="outlined"
            size="sm"
          >
            <Option value="By score (ASC)">By score (ASC)</Option>
            <Option value="By score (DESC)">By score (DESC)</Option>
          </Select>
        </Box> */}
      </Stack>

      <Candidates job={selectedJob} basePath="/dashboard" user={user} startDate={startDate} endDate={endDate} />
    </Box>
  );
};

export default Dashboard;
